import React, { useEffect, useState } from "react";
import { X, Loader2, Save, Send, Mail, Truck, Wallet, ClipboardList, History, Receipt, Trash2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "./api";
import { generateBoxInvoicePdf } from "../lib/invoicePdf";
import RefundCreditModal from "./RefundCreditModal";
import { OrderMessagesCard } from "./OrderMessagesCard";
import { useTuberAudit } from "./useTuberAudit";
import { FulfilmentPanel, HoldPanel } from "./FulfilmentPanel";

const SHIPPING_STATUS_OPTIONS = [
  { v: "tracking-assigned", label: "Tracking assigned" },
  { v: "with-courier",      label: "With NZ Post" },
  { v: "delivered",         label: "Delivered" },
];

const PAYMENT_STATUSES = [
  { v: "unpaid",  label: "Unpaid",  tone: "bg-[#A33030] text-white" },
  { v: "partial", label: "Partial", tone: "bg-[#C49247] text-white" },
  { v: "paid",    label: "Paid",    tone: "bg-[#2F4A2A] text-white" },
];

const PAYMENT_METHODS = [
  { v: "",              label: "-" },
  { v: "bank-transfer", label: "Bank transfer" },
  { v: "card",          label: "Card" },
  { v: "cash",          label: "Cash" },
  { v: "credit",        label: "HQ credit" },
];

/**
 * ConsoleOrderDrawer , slide-over for every console_order.
 * Shows full details, inline payment toggle, shipping editor with email copy
 * template, complete stage history, and a "Download invoice PDF" button.
 */
export default function ConsoleOrderDrawer({ orderId, onClose, onUpdated }) {
  const auditQty = useTuberAudit();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [emailingTracking, setEmailingTracking] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [editCustomer, setEditCustomer] = useState(false);
  const [editItems, setEditItems] = useState(false);
  const [customerDraft, setCustomerDraft] = useState({});
  const [itemsDraft, setItemsDraft] = useState([]);
  const [shippingPriceDraft, setShippingPriceDraft] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/console/orders/${orderId}`);
        setOrder(r.data);
      } catch {
        toast.error("Couldn't load order.");
      } finally { setLoading(false); }
    })();
  }, [orderId]);

  const patchOrder = async (patch, message) => {
    setBusy(true);
    try {
      const merged = { ...order, ...patch };
      // The PATCH /console/orders/{id} endpoint expects the full OrderIn shape.
      const r = await api.patch(`/console/orders/${orderId}`, {
        customer_id: merged.customer_id,
        customer_name: merged.customer_name,
        email: merged.email,
        phone: merged.phone,
        address: merged.address,
        line_items: merged.line_items,
        shipping_price: merged.shipping_price,
        shipping_provider: merged.shipping_provider,
        notes: merged.notes,
        terms: merged.terms,
        payment_method: merged.payment_method,
        payment_status: merged.payment_status,
        hq_picks_note: merged.hq_picks_note,
        hq_pick_count: merged.hq_pick_count,
      });
      setOrder(r.data);
      onUpdated && onUpdated(r.data);
      if (message) toast.success(message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const updateShipping = async (patch) => {
    setBusy(true);
    try {
      const r = await api.post(`/console/orders/${orderId}/shipping`, patch);
      setOrder(r.data);
      onUpdated && onUpdated(r.data);
      toast.success("Shipping updated.");
    } catch (e) {
      toast.error("Couldn't update shipping.");
    } finally { setBusy(false); }
  };

  const emailTracking = async () => {
    setEmailingTracking(true);
    try {
      const r = await api.post(`/console/orders/${orderId}/email-tracking`);
      toast.success(`Tracking emailed to ${r.data.sent_to}.`);
      const fresh = await api.get(`/console/orders/${orderId}`);
      setOrder(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send tracking email.");
    } finally { setEmailingTracking(false); }
  };

  const downloadInvoice = async () => {
    if (!order) return;
    try {
      const today = new Date();
      const due = new Date(today.getTime() + 7 * 86400 * 1000);
      const iso = (d) => d.toISOString().slice(0, 10);
      const inv = `HD-${iso(today).replace(/-/g, "")}-${(order.id || "").slice(0, 4).toUpperCase()}`;
      const items = (order.line_items || []).map((li) => ({
        description: li.name + (li.source === "inventory" ? " (heritage stock)" : ""),
        qty: Number(li.qty || 1),
        unit_price: Number(li.unit_price || 0),
      }));
      if (Number(order.shipping_price) > 0) {
        items.push({ description: `Shipping (${order.shipping_provider || "NZ Post"})`, qty: 1, unit_price: Number(order.shipping_price) });
      }
      const result = generateBoxInvoicePdf({
        invoiceNumber: inv,
        issueDate: iso(today),
        dueDate: iso(due),
        customer: { name: order.customer_name, email: order.email, phone: order.phone, address: order.address },
        items,
        subtotal: Number(order.total || 0),
        creditApplied: 0,
        total: Number(order.total || 0),
        notes: order.notes || "",
        download: false,
      });
      // Stamp the rendered PDF onto the order so anyone can download it by choice.
      try {
        const r = await api.post(`/console/orders/${order.id}/invoice`, {
          pdf_base64: result.base64,
          filename: result.filename,
          total: result.total,
        });
        setOrder((o) => ({ ...o, invoice: { ...result, version: r.data.version, saved_at: r.data.saved_at } }));
        toast.success(`Invoice v${r.data.version} rendered & saved , use "Saved invoice" to download it.`);
      } catch {
        toast.error("Invoice rendered but couldn't be saved to the order.");
      }
    } catch { toast.error("Couldn't render invoice."); }
  };

  const downloadSavedInvoice = async () => {
    if (!order?.invoice) return;
    const a = document.createElement("a");
    a.href = `data:application/pdf;base64,${order.invoice.pdf_base64}`;
    a.download = order.invoice.filename || `invoice-${order.id.slice(0, 8)}.pdf`;
    a.click();
  };

  const [emailingInvoice, setEmailingInvoice] = React.useState(false);
  const emailInvoiceToCustomer = async () => {
    if (!order?.email) return;
    setEmailingInvoice(true);
    try {
      const r = await api.post(`/console/orders/${order.id}/email-invoice`, { origin_url: window.location.origin });
      toast.success(`Invoice emailed to ${r.data?.sent_to || order.email}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't email the invoice.");
    } finally { setEmailingInvoice(false); }
  };

  const deleteOrder = async () => {
    if (!order) return;
    const label = order.order_number ? `#${order.order_number}` : `for ${order.customer_name}`;
    if (!window.confirm(`Delete this order (${label})? This cannot be undone , it removes the order from every tab + financial summary.`)) return;
    setBusy(true);
    try {
      await api.delete(`/console/orders/${order.id}`);
      toast.success("Order deleted.");
      onUpdated && onUpdated({ ...order, _deleted: true });
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="order-drawer">
      <button type="button" onClick={onClose} className="absolute inset-0 bg-black/30" aria-label="Close" />
      <aside className="relative w-full max-w-2xl bg-[#F8F4EA] h-full overflow-y-auto shadow-2xl">
        <header className="sticky top-0 z-10 bg-[#F8F4EA] border-b border-[#E8DFCB] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold">Order detail</div>
            {order && (
              <div className="font-serif text-2xl text-[#3C2A1E]">
                #{order.id?.slice(0, 8)} , {order.customer_name}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#E8DFCB] rounded" data-testid="order-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        {loading || !order ? (
          <div className="p-6 text-[#7A6A55] inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Danger zone , delete button at the top so admin can wipe test
                orders without scrolling. Confirms before destroying. */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={deleteOrder}
                disabled={busy}
                data-testid="order-drawer-delete"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] tracking-[0.25em] uppercase font-semibold border"
                style={{ borderColor: "#A33030", color: "#A33030" }}
                title="Delete this order permanently"
              >
                <Trash2 className="w-3 h-3" />
                Delete order
              </button>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Stage" value={order.stage} tone="berry" />
              <Stat label="Total NZ$" value={`$${Number(order.total || 0).toFixed(2)}`} tone="forest" />
              <Stat label="Payment" value={order.payment_status || "unpaid"} tone={order.payment_status === "paid" ? "forest" : order.payment_status === "partial" ? "amber" : "red"} />
            </div>

            {/* Customer */}
            <Section
              title="Customer"
              icon={Mail}
              right={
                !editCustomer ? (
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerDraft({
                        customer_name: order.customer_name || "",
                        email: order.email || "",
                        phone: order.phone || "",
                        address: order.address || "",
                      });
                      setEditCustomer(true);
                    }}
                    data-testid="drawer-edit-customer"
                    className="text-[10px] tracking-[0.2em] uppercase text-[#6E1E3A] hover:underline inline-flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                ) : null
              }
            >
              {!editCustomer ? (
                <>
                  <Row label="Name">{order.customer_name}</Row>
                  <Row label="Email">{order.email || "-"}</Row>
                  <Row label="Phone">{order.phone || "-"}</Row>
                  <Row label="Address"><span className="whitespace-pre-wrap">{order.address || "-"}</span></Row>
                </>
              ) : (
                <div className="space-y-2.5">
                  <FieldRow label="Name">
                    <input type="text" value={customerDraft.customer_name} onChange={(e) => setCustomerDraft({ ...customerDraft, customer_name: e.target.value })} data-testid="drawer-edit-customer-name" className="w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />
                  </FieldRow>
                  <FieldRow label="Email">
                    <input type="email" value={customerDraft.email} onChange={(e) => setCustomerDraft({ ...customerDraft, email: e.target.value })} data-testid="drawer-edit-customer-email" className="w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />
                  </FieldRow>
                  <FieldRow label="Phone">
                    <input type="tel" value={customerDraft.phone} onChange={(e) => setCustomerDraft({ ...customerDraft, phone: e.target.value })} data-testid="drawer-edit-customer-phone" className="w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />
                  </FieldRow>
                  <FieldRow label="Address">
                    <textarea rows={3} value={customerDraft.address} onChange={(e) => setCustomerDraft({ ...customerDraft, address: e.target.value })} data-testid="drawer-edit-customer-address" className="w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded" />
                  </FieldRow>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        await patchOrder(customerDraft, "Customer details updated.");
                        setEditCustomer(false);
                      }}
                      disabled={busy}
                      data-testid="drawer-save-customer"
                      className="px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditCustomer(false)}
                      className="px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold border border-[#E8DFCB] text-[#7A6A55] hover:bg-white rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </Section>

            {/* Line items */}
            <Section
              title="Items"
              icon={ClipboardList}
              right={
                !editItems ? (
                  <button
                    type="button"
                    onClick={() => {
                      setItemsDraft((order.line_items || []).map((li) => ({
                        name: li.name || "",
                        qty: Number(li.qty || 1),
                        unit_price: Number(li.unit_price || 0),
                        variety_id: li.variety_id || null,
                        source: li.source || "custom",
                      })));
                      setShippingPriceDraft(Number(order.shipping_price || 0));
                      setEditItems(true);
                    }}
                    data-testid="drawer-edit-items"
                    className="text-[10px] tracking-[0.2em] uppercase text-[#6E1E3A] hover:underline inline-flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                ) : null
              }
            >
              {!editItems ? (
                <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">
                  <tr><th className="text-left py-1">Item</th><th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-right">Subtotal</th></tr>
                </thead>
                <tbody className="text-[#3C2A1E]">
                  {(order.line_items || []).map((li, i) => (
                    <tr key={i} className="border-t border-[#F4ECD8]">
                      <td className="py-1.5">{li.name}{li.source === "inventory" && <span className="ml-1 text-[10px] text-[#7A6A55]">· stock</span>}{auditQty(li.name) !== null && <span className="ml-1.5 text-[10px] font-semibold text-[#2F6B4F]" data-testid={`drawer-audit-count-${i}`}>· {auditQty(li.name)} in audit</span>}</td>
                      <td className="text-right">{li.qty}</td>
                      <td className="text-right">${Number(li.unit_price).toFixed(2)}</td>
                      <td className="text-right">${(Number(li.qty || 1) * Number(li.unit_price || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-[#E8DFCB] text-[#7A6A55]">
                    <td colSpan={3} className="text-right text-[11px] pt-2">Shipping ({order.shipping_provider})</td>
                    <td className="text-right pt-2">${Number(order.shipping_price || 0).toFixed(2)}</td>
                  </tr>
                  <tr className="font-serif text-base text-[#3C2A1E]">
                    <td colSpan={3} className="text-right">Total</td>
                    <td className="text-right">${Number(order.total || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
              ) : (
                <div className="space-y-2">
                  {itemsDraft.map((li, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`drawer-edit-item-${i}`}>
                      <div className="col-span-6">
                        <input
                          type="text"
                          value={li.name}
                          onChange={(e) => setItemsDraft(itemsDraft.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                          placeholder="Item name"
                          className="w-full px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded"
                          data-testid={`drawer-edit-item-name-${i}`}
                        />
                        {auditQty(li.name) !== null && (
                          <div className="text-[10px] font-semibold text-[#2F6B4F] mt-0.5" data-testid={`drawer-edit-audit-count-${i}`}>
                            Tuber Audit: {auditQty(li.name)} counted
                          </div>
                        )}
                      </div>
                      <input
                        type="number"
                        min={1}
                        value={li.qty}
                        onChange={(e) => setItemsDraft(itemsDraft.map((x, j) => j === i ? { ...x, qty: Math.max(1, Math.floor(Number(e.target.value) || 1)) } : x))}
                        className="col-span-2 px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded text-right"
                        data-testid={`drawer-edit-item-qty-${i}`}
                      />
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={li.unit_price}
                        onChange={(e) => setItemsDraft(itemsDraft.map((x, j) => j === i ? { ...x, unit_price: Math.max(0, Number(e.target.value) || 0) } : x))}
                        className="col-span-3 px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded text-right"
                        data-testid={`drawer-edit-item-price-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => setItemsDraft(itemsDraft.filter((_, j) => j !== i))}
                        className="col-span-1 p-1.5 text-[#A33030] hover:bg-[#FFF4F0] rounded"
                        data-testid={`drawer-edit-item-delete-${i}`}
                        title="Remove this line"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setItemsDraft([...itemsDraft, { name: "", qty: 1, unit_price: 0, source: "custom" }])}
                    data-testid="drawer-edit-item-add"
                    className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase text-[#6E1E3A] border border-dashed border-[#6E1E3A]/40 hover:bg-[#6E1E3A]/5 rounded inline-flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add line
                  </button>
                  <div className="grid grid-cols-12 gap-1.5 items-center pt-2 border-t border-[#E8DFCB]">
                    <label className="col-span-8 text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold text-right pr-2">Shipping price (NZ$)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={shippingPriceDraft}
                      onChange={(e) => setShippingPriceDraft(Math.max(0, Number(e.target.value) || 0))}
                      className="col-span-3 px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded text-right"
                      data-testid="drawer-edit-shipping-price"
                    />
                    <div className="col-span-1" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={async () => {
                        const cleaned = itemsDraft
                          .map((x) => ({ ...x, name: (x.name || "").trim() }))
                          .filter((x) => x.name && x.qty > 0);
                        if (cleaned.length === 0) {
                          toast.error("At least one line item with a name and qty is required.");
                          return;
                        }
                        await patchOrder({ line_items: cleaned, shipping_price: shippingPriceDraft }, "Items + shipping saved.");
                        setEditItems(false);
                      }}
                      disabled={busy}
                      data-testid="drawer-save-items"
                      className="px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      <Save className="w-3 h-3" /> Save items
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditItems(false)}
                      className="px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold border border-[#E8DFCB] text-[#7A6A55] hover:bg-white rounded"
                    >
                      Cancel
                    </button>
                    <div className="ml-auto text-sm text-[#7A6A55] self-center">
                      Subtotal: <strong className="text-[#3C2A1E]">${itemsDraft.reduce((sum, x) => sum + Number(x.qty || 0) * Number(x.unit_price || 0), 0).toFixed(2)}</strong>
                      {" · "}Total: <strong className="text-[#3C2A1E]">${(itemsDraft.reduce((sum, x) => sum + Number(x.qty || 0) * Number(x.unit_price || 0), 0) + Number(shippingPriceDraft || 0)).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              )}
              {Number(order.hq_pick_count || 0) > 0 && (
                <div className="mt-2 px-2 py-1.5 bg-[#6E1E3A]/8 border border-[#6E1E3A]/20 rounded text-[12px] text-[#6E1E3A] flex items-center justify-between gap-2">
                  <span><strong>{order.hq_pick_count}</strong> tuber{order.hq_pick_count === 1 ? "" : "s"} to pick from HQ catalogue</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      const v = window.prompt("HQ pick allowance (tubers)", String(order.hq_pick_count || 0));
                      if (v === null) return;
                      const n = Math.max(0, Math.floor(Number(v) || 0));
                      patchOrder({ hq_pick_count: n }, `HQ pick allowance set to ${n}.`);
                    }}
                    data-testid="drawer-hq-pick-edit"
                    className="text-[10px] tracking-[0.2em] uppercase text-[#6E1E3A] hover:underline"
                  >
                    Edit
                  </button>
                </div>
              )}
              {Number(order.hq_pick_count || 0) === 0 && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const v = window.prompt("HQ pick allowance (tubers)", "0");
                    if (v === null) return;
                    const n = Math.max(0, Math.floor(Number(v) || 0));
                    if (n > 0) patchOrder({ hq_pick_count: n }, `HQ pick allowance set to ${n}.`);
                  }}
                  data-testid="drawer-hq-pick-add"
                  className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A] hover:underline"
                >
                  + Add HQ pick allowance
                </button>
              )}
              {Number(order.hq_pick_count || 0) > 0 && (order.hq_picked_varieties || []).length > 0 && (
                <div className="mt-2 px-2 py-1.5 bg-green-50 border border-green-200 rounded text-[12px] text-green-900" data-testid="drawer-customer-picks">
                  <div className="font-semibold text-[10px] tracking-[0.25em] uppercase mb-1">✓ Customer picks in{order.hq_picks_submitted_at ? ` · ${new Date(order.hq_picks_submitted_at).toLocaleDateString("en-NZ")}` : ""}</div>
                  <div className="flex flex-wrap gap-1">
                    {order.hq_picked_varieties.map((v) => (
                      <span key={v.id} className="px-2 py-0.5 bg-white border border-green-300 rounded-full text-[11px]">{v.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {order.hq_picks_note && (
                <div className="mt-2 px-2 py-1.5 bg-[#6E1E3A]/5 border border-[#6E1E3A]/15 rounded text-[11px] text-[#6E1E3A] italic">Heritage note: {order.hq_picks_note}</div>
              )}
            </Section>

            {/* Payment quick edit */}
            <Section title="Payment" icon={Wallet}>
              <div className="flex gap-2">
                {PAYMENT_STATUSES.map((s) => (
                  <button key={s.v} type="button" disabled={busy} onClick={() => patchOrder({ payment_status: s.v }, `Marked ${s.label}.`)}
                    data-testid={`drawer-payment-${s.v}`}
                    className={`flex-1 py-1.5 text-[11px] tracking-[0.2em] uppercase rounded ${order.payment_status === s.v ? s.tone : "bg-white border border-[#E8DFCB] text-[#7A6A55] hover:bg-[#FBF6E9]"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">Method</label>
                <select
                  value={order.payment_method || ""}
                  onChange={(e) => patchOrder({ payment_method: e.target.value }, "Payment method updated.")}
                  data-testid="drawer-payment-method"
                  disabled={busy}
                  className="flex-1 px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded"
                >
                  {PAYMENT_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
              </div>
            </Section>

            {/* Fulfilment */}
            <Section title="Fulfilment" icon={Truck}>
              <div className="mb-3"><HoldPanel order={order} onChanged={async () => { const r = await api.get(`/console/orders/${orderId}`); setOrder(r.data); onUpdated && onUpdated(r.data); }} /></div>
              <FulfilmentPanel order={order} onChanged={(fresh) => { if (fresh && fresh.id) { setOrder(fresh); onUpdated && onUpdated(fresh); } }} />
            </Section>

            {/* Notes */}
            <Section title="Notes">
              <textarea rows={3} defaultValue={order.notes || ""} onBlur={(e) => e.target.value !== (order.notes || "") && patchOrder({ notes: e.target.value }, "Notes saved.")} data-testid="drawer-notes" className="w-full p-3 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] leading-relaxed focus:outline-none focus:border-[#6E1E3A]" />
            </Section>

            {/* Stage history */}
            <Section title="Stage history" icon={History}>
              <ol className="space-y-1.5 text-xs text-[#3C2A1E]">
                {(order.stage_history || []).map((h, i) => (
                  <li key={i} className="flex justify-between border-l-2 border-[#6E1E3A]/30 pl-3">
                    <span className="font-medium">{h.stage}</span>
                    <span className="text-[#7A6A55]">{new Date(h.at).toLocaleString("en-NZ")}{h.via ? ` · ${h.via}` : ""}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-[#F8F4EA] border-t border-[#E8DFCB] flex flex-wrap gap-2">
              <button type="button" onClick={downloadInvoice} data-testid="drawer-download-invoice" className="px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded inline-flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5" /> {order.invoice ? `Re-render invoice (v${(order.invoice.version || 0) + 1})` : "Render & save invoice"}
              </button>
              {order.invoice && (
                <button type="button" onClick={downloadSavedInvoice} data-testid="drawer-download-saved-invoice" className="px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold bg-white border border-[#6E1E3A] text-[#6E1E3A] hover:bg-[#6E1E3A]/5 rounded inline-flex items-center gap-1.5">
                  ↓ Saved invoice (v{order.invoice.version})
                </button>
              )}
              {order.email && order.payment_status !== "paid" && (
                <button
                  type="button"
                  onClick={emailInvoiceToCustomer}
                  disabled={emailingInvoice}
                  data-testid="drawer-email-invoice"
                  className="px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold bg-white border border-[#3C2A1E] text-[#3C2A1E] hover:bg-[#3C2A1E]/5 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {emailingInvoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Email invoice
                </button>
              )}
              {order.payment_status === "paid" && (
                <button
                  type="button"
                  onClick={() => setShowRefund(true)}
                  data-testid="drawer-refund-btn"
                  className="px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold bg-white border border-[#A33030] text-[#A33030] hover:bg-[#A33030]/5 rounded inline-flex items-center gap-1.5 ml-auto"
                >
                  <Receipt className="w-3.5 h-3.5" /> Refund / Credit
                </button>
              )}
            </div>
          </div>
        )}

        {order && <OrderMessagesCard orderId={orderId} />}

        {showRefund && order && (
          <RefundCreditModal
            order={order}
            onClose={() => setShowRefund(false)}
            onDone={() => { onUpdated?.(); api.get(`/console/orders/${orderId}`).then((r) => setOrder(r.data)); }}
          />
        )}
      </aside>
    </div>
  );
}

function Section({ title, icon: Icon, children, right }) {
  return (
    <section className="bg-white border border-[#E8DFCB] rounded-md p-4">
      <header className="text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold mb-2.5 flex items-center justify-between gap-1.5">
        <span className="inline-flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" />} {title}
        </span>
        {right}
      </header>
      {children}
    </section>
  );
}

function Stat({ label, value, tone = "berry" }) {
  const cls = {
    berry: "bg-[#6E1E3A] text-white",
    forest: "bg-[#2F4A2A] text-white",
    amber: "bg-[#C49247] text-white",
    red: "bg-[#A33030] text-white",
  }[tone];
  return (
    <div className={`${cls} rounded p-2.5`}>
      <div className="text-[9px] tracking-[0.3em] uppercase opacity-85">{label}</div>
      <div className="font-serif text-lg mt-0.5 capitalize">{value}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span className="text-[#9C8B73] text-[11px] tracking-[0.2em] uppercase font-semibold">{label}</span>
      <span className="text-[#3C2A1E] text-right max-w-[60%]">{children}</span>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">
      {label}
      <div className="mt-1 font-normal normal-case tracking-normal">{children}</div>
    </label>
  );
}
